// src/components/AboutSection.tsx
//
// Home page professional snapshot.
//
// This is a qualification section, not a biography. It gives a visitor enough
// to judge fit — backend-focused, 4.5+ years, enterprise applications, four
// real business domains — and then sends them to /about for the detail. The
// domain narratives, engineering approach and full timeline live in
// aboutConfig.ts and are deliberately not restated here.
//
// `id="about"` was removed from the root. It collided with AboutPage.tsx, which
// owns that id, and it made this Home section masquerade as the /about route -
// which is also why the footer's old `#about` link appeared to work here.
import { useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { homeConfig } from '../config/homeConfig';
import { STAGGER, motionSet } from './about/motion';
import {
  SnapshotRoot,
  SnapshotContainer,
  StatementCol,
  Eyebrow,
  Heading,
  Statement,
  SnapshotCta,
  SpecSheet,
  SpecLabel,
  SpecList,
  SpecRow,
  SpecTerm,
  SpecValue,
  DomainsBlock,
  DomainsHead,
  DomainsCount,
  DomainsLabel,
  DomainsList,
  DomainsItem,
} from './AboutSection.styles';

const headingId = 'home-snapshot-heading';

// Matches every other Home section: reveal once, on a 0.3 threshold.
const viewport = { once: true, amount: 0.3 } as const;

const AboutSection = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion() ?? false;

  const { snapshot } = homeConfig;
  const motion = motionSet(reduceMotion);
  const rise = motion.reveal;

  return (
    <SnapshotRoot aria-labelledby={headingId}>
      <SnapshotContainer>
        <StatementCol
          variants={motion.group(STAGGER.group)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          <Eyebrow variants={rise}>{snapshot.eyebrow}</Eyebrow>
          <Heading id={headingId} variants={rise}>
            {snapshot.heading}
          </Heading>
          <Statement variants={rise}>{snapshot.statement}</Statement>
          <SnapshotCta variants={rise} type="button" onClick={() => navigate(snapshot.cta.to)}>
            {snapshot.cta.label}
          </SnapshotCta>
        </StatementCol>

        <SpecSheet variants={rise} initial="hidden" whileInView="visible" viewport={viewport}>
          <SpecLabel>{snapshot.snapshotLabel}</SpecLabel>
          <SpecList>
            {snapshot.rows.map((row) => (
              <SpecRow key={row.label}>
                <SpecTerm>{row.label}</SpecTerm>
                <SpecValue>{row.value}</SpecValue>
              </SpecRow>
            ))}
          </SpecList>

          <DomainsBlock>
            <DomainsHead>
              <DomainsCount>{snapshot.domains.length}</DomainsCount>
              <DomainsLabel>Business domains</DomainsLabel>
            </DomainsHead>
            <DomainsList>
              {snapshot.domains.map((domain) => (
                <DomainsItem key={domain}>{domain}</DomainsItem>
              ))}
            </DomainsList>
          </DomainsBlock>
        </SpecSheet>
      </SnapshotContainer>
    </SnapshotRoot>
  );
};

export default AboutSection;
